/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Metrics", description = "Learn how to create custom metrics to use in Campaign Intelligence.", lastUpdatedDate = new Date(1733184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Metrics",
        description: "Learn how to create custom metrics to use in Campaign Intelligence.",
        lastUpdatedDate: "Tue Dec 03 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Creating a Metric",
          "depth": 1,
          "id": "creating-a-metric"
        }, {
          "value": "Adding an Attribution Method",
          "depth": 1,
          "id": "adding-an-attribution-method"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      img: "img",
      h3: "h3",
      ul: "ul",
      li: "li",
      strong: "strong"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Campaign Intelligence is available as an add-on on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "A metric is a quantifiable measure used to track and assess the status of a specific business process. Metrics help in understanding performance and identifying areas for improvement. Key components of a metric are shown below:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/attr_metric.png",
          alt: "Chart Anatomy"
        })
      }), "\n", _jsx(_components.h3, {
        id: "creating-a-metric",
        children: "Creating a Metric"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: _jsx(_components.strong, {
              children: "Event"
            })
          }), "\n", _jsx(_components.p, {
            children: "An event is a specific action or occurrence that can be measured. Examples include a user clicking a button, a transaction being completed, or a user becoming a subscriber."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: _jsx(_components.strong, {
              children: "Aggregation Type"
            })
          }), "\n", _jsx(_components.p, {
            children: "The aggregation type defines how individual events are combined to form the metric. Our aggregations include sum, count, unique count, and average."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: _jsx(_components.strong, {
              children: "Filter"
            })
          }), "\n", _jsx(_components.p, {
            children: "Filters are used to narrow down the data included in a metric. Filters can be based on various criteria such as location, product type, or price."
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "adding-an-attribution-method",
        children: "Adding an Attribution Method"
      }), "\n", _jsx(_components.p, {
        children: "While metrics can stand alone, you must apply an attribution method to tie them back to a campaign, enabling you to understand the impact of different marketing efforts on these metrics."
      }), "\n", _jsxs(_components.p, {
        children: ["Metrics can have multiple attribution methods. This is useful for gaining a more comprehensive understanding of how different events contribute to conversions. Learn more about attribution ", _jsx(_components.a, {
          href: "/campaign-intelligence/attribution-methods",
          children: "here"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
